import React, { memo } from 'react'
import classNames from 'classnames'
import LaneArrowIcon from 'transfix-icons/build/LaneArrowIcon'
import { Box, Skeleton } from 'transfix-ui/core'
import { IFullStop, STOP_TYPE } from 'types'
import { useStopSummaryStyles } from 'apps/fleet-planner/components/load-details/stops//StopsSummaryStyles'
import { StopsSummaryConnector } from 'apps/fleet-planner/components/load-details/stops/StopsSummaryConnector'
import { MarketplaceStopSummaryItem } from './MarketplaceStopSummaryItem'

interface IMarketplaceMarketplaceStopSummaryProps {
  stops?: IFullStop[]
  className?: string
  compact?: boolean
  /**
   * @default false
   */
  alwaysDisplayYear?: boolean
}

// Methods below should eventually be replaced with the ones in src/apps/fleet-planner/utils/stops.ts
// once these MP values are updated to more closely match Fleet-Planners Shipment schema
const getPickupCount = (stops: IFullStop[] = []): number => {
  const filteredStops = stops.filter(({ type }) => type === STOP_TYPE.pickup)
  return filteredStops.length
}

const getDropoffCount = (stops: IFullStop[] = []): number => {
  const filteredStops = stops.filter(({ type }) => type === STOP_TYPE.delivery)
  return filteredStops.length
}

const getStopNumber = (stop?: IFullStop, allStops: IFullStop[] = []): number => {
  const pickupCount = getPickupCount(allStops)
  const dropoffCount = getDropoffCount(allStops)

  const { stopSequence = 1, type } = stop || {}
  let stopNumber = 0
  if (type === STOP_TYPE.pickup && pickupCount > 1) {
    stopNumber = stopSequence
  } else if (type === STOP_TYPE.delivery && dropoffCount > 1) {
    stopNumber = stopSequence - pickupCount
  }

  return stopNumber
}

export const MarketplaceStopSummary = memo<IMarketplaceMarketplaceStopSummaryProps>(
  ({ stops = [], className, compact = false, alwaysDisplayYear = false }) => {
    const styles = useStopSummaryStyles({ compact })
    const classes = classNames(styles.root, className)
    const [firstStop] = stops || []
    const lastStop =
      stops.find(stop => stop.type === STOP_TYPE.delivery && stop.stopSequence === stops.length) ||
      stops[stops.length - 1]

    const firstStopNumber = getStopNumber(firstStop, stops)
    const lastStopNumber = getStopNumber(lastStop, stops)
    const hasLoaded = !!stops.length

    return (
      <div className={classes} data-test='stops-summary'>
        {hasLoaded ? (
          <>
            <MarketplaceStopSummaryItem
              alwaysDisplayYear={alwaysDisplayYear}
              stop={firstStop}
              stopNumber={firstStopNumber}
              compact={compact}
            />

            {/* Desktop Only */}
            <div className={styles.icon}>
              <LaneArrowIcon size='xlarge' color='coolGray5' />
            </div>

            {/* Mobile only */}
            <StopsSummaryConnector stops={stops} compact={compact} />

            <MarketplaceStopSummaryItem
              alwaysDisplayYear={alwaysDisplayYear}
              stop={lastStop}
              stopNumber={lastStopNumber}
              compact={compact}
            />
          </>
        ) : (
          <>
            <Box>
              <Box marginBottom='3px'>
                <Skeleton variant='rectangular' height={20} width={190} />
              </Box>
              <Skeleton variant='rectangular' height={22} width={145} />
            </Box>

            <Box className={styles.icon}>
              <LaneArrowIcon size='xlarge' color='coolGray5' />
            </Box>
            <Box>
              <Box marginBottom='3px'>
                <Skeleton variant='rectangular' height={20} width={190} />
              </Box>
              <Skeleton variant='rectangular' height={22} width={145} />
            </Box>
          </>
        )}
      </div>
    )
  }
)
